import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { runCoreAction, type CoreActionResult } from '@/lib/coreActions';
import { generateIdempotencyKey } from '@/lib/coreIdempotency';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 46/47. PONT ACTION LAYER → CRÉATIONS RÉELLES
// Les modules métier (commandes, factures, réservations…) n'ont plus à
// journaliser ni dédupliquer à la main : ils passent par cette passerelle
// qui résout l'organisation, applique l'idempotence et enregistre l'action.
//
//   dispatchCoreAction({
//     commerceId, actionKey: 'create_order', entityType: 'order',
//     idempotencyKey, permission: 'orders.write', canAccess,
//     execute: async () => { ... création réelle ... return { entityId }; }
//   })
//
// Ne lève jamais : renvoie un CoreActionResult normalisé (success /
// duplicate / denied / failed). Le résultat `duplicate` signifie que
// l'opération avait déjà été exécutée (rejeu réseau / double clic).
// ─────────────────────────────────────────────────────────────

export interface CoreActionBridgeExecuteContext {
  tenantId: number | null;
  organizationId: number | null;
  /** Clé d'idempotence effective (null si l'idempotence est désactivée). */
  idempotencyKey: string | null;
}

export interface CoreActionBridgeInput {
  /** Id du marchand (users.id / idvendeur). Sert à résoudre l'organisation Core. */
  commerceId: number | null | undefined;
  actionKey: string;
  entityType: string;
  resourceType?: string | null;
  resourceId?: string | number | null;
  permission?: string | null;
  canAccess?: (permission: string) => boolean;
  parameters?: Record<string, unknown> | null;
  /** Fourni → active l'idempotence (spec 47). */
  idempotencyKey?: string | null;
  idempotencyScope?: string | null;
  source?: string | null;
  actorUserId?: number | null;
  actorName?: string | null;
  execute: (ctx: CoreActionBridgeExecuteContext) => Promise<{
    entityId?: string | number | null;
    result?: unknown;
  }>;
}

/** Exécute une création métier via l'Action Layer du socle. */
export async function dispatchCoreAction(input: CoreActionBridgeInput): Promise<CoreActionResult> {
  const idempotencyKey = input.idempotencyKey ?? null;

  // Résolution de l'organisation : journalisation + idempotence en ont besoin.
  let tenantId: number | null = null;
  let organizationId: number | null = null;
  try {
    if (input.commerceId) {
      const org = await getOrganizationByCommerce(input.commerceId);
      if (org) {
        tenantId = org.tenant_id ?? null;
        organizationId = org.id;
      }
    }
  } catch {
    /* best-effort : on retombe sur une exécution sans journalisation */
  }

  // Pas d'organisation Core : on exécute quand même (compte legacy), sans
  // journaliser ni dédupliquer — le métier ne doit jamais être bloqué.
  if (organizationId == null) {
    try {
      const exec = await input.execute({ tenantId, organizationId: null, idempotencyKey: null });
      return {
        ok: true,
        status: 'success',
        actionId: '',
        entityType: input.entityType,
        entityId: exec.entityId != null ? String(exec.entityId) : null,
        result: exec.result ?? null,
        durationMs: 0,
      };
    } catch (e) {
      return {
        ok: false,
        status: 'failed',
        actionId: '',
        entityType: null,
        entityId: null,
        result: null,
        error: e instanceof Error ? e.message : 'Erreur d’exécution.',
        durationMs: 0,
      };
    }
  }

  return runCoreAction({
    actionKey: input.actionKey,
    tenantId,
    organizationId,
    actorUserId: input.actorUserId ?? null,
    actorName: input.actorName ?? null,
    resourceType: input.resourceType ?? input.entityType,
    resourceId: input.resourceId ?? null,
    permission: input.permission ?? null,
    parameters: input.parameters ?? null,
    canAccess: input.canAccess,
    idempotencyKey,
    idempotencyScope: input.idempotencyScope ?? null,
    source: input.source ?? 'module',
    execute: async () => {
      const exec = await input.execute({ tenantId, organizationId, idempotencyKey });
      return {
        entityType: input.entityType,
        entityId: exec.entityId ?? null,
        result: exec.result ?? null,
      };
    },
  });
}

/** Génère une clé d'idempotence à mémoriser le temps d'un envoi (retry-safe). */
export function newIdempotencyKey(prefix = 'idem'): string {
  return generateIdempotencyKey(prefix);
}